import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from "react";
import {
  calculateTokenExpiry,
  storeTokenWithExpiry,
  GUEST_TOKEN_EXPIRY_SECONDS,
  VERIFIED_TOKEN_EXPIRY_SECONDS,
} from "~/utils/token";

const TOKEN_STORAGE_KEY = "x_pebble_demo_token";
const EMAIL_STORAGE_KEY = "x_pebble_demo_email";
const EMAIL_LOCAL_STORAGE_KEY = "x_pebble_demo_email_persistent"; // For cross-tab persistence
const TOKEN_EXPIRY_KEY = "x_pebble_demo_token_expiry";
const VERIFICATION_STATUS_KEY = "x_pebble_demo_verification";
const VERIFICATION_LOCAL_STORAGE_KEY = "x_pebble_demo_verified_emails"; // Track verified emails
const SESSION_ID_KEY = "x_pebble_demo_session";

interface AuthContextType {
  token: string | null;
  email: string | null;
  isAuthenticated: boolean;
  isEmailVerified: boolean;
  verificationState: "initial" | "pending" | "verified" | "error";
  isLoading: boolean;
  isInitializing: boolean;
  error: string | null;
  submitEmail: (email: string) => Promise<void>;
  verifyEmail: (verificationToken: string) => Promise<void>;
  resendVerificationEmail: () => Promise<void>;
  refreshToken: () => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

// Helper functions for managing verified emails
const getVerifiedEmails = (): string[] => {
  try {
    const stored = localStorage.getItem(VERIFICATION_LOCAL_STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
};

const addVerifiedEmail = (email: string) => {
  const verified = getVerifiedEmails();
  if (!verified.includes(email)) {
    verified.push(email);
    localStorage.setItem(
      VERIFICATION_LOCAL_STORAGE_KEY,
      JSON.stringify(verified)
    );
  }
};

const isEmailPreviouslyVerified = (email: string): boolean => {
  return getVerifiedEmails().includes(email);
};

export function AuthProvider({ children }: { children: ReactNode }) {
  const [token, setToken] = useState<string | null>(null);
  const [email, setEmail] = useState<string | null>(null);
  const [isEmailVerified, setIsEmailVerified] = useState(false);
  const [verificationState, setVerificationState] = useState<
    "initial" | "pending" | "verified" | "error"
  >("initial");
  const [isLoading, setIsLoading] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Generate or get session ID for browser session scoping
  const getSessionId = () => {
    let sessionId = sessionStorage.getItem(SESSION_ID_KEY);
    if (!sessionId) {
      sessionId = `session_${Date.now()}_${Math.random()
        .toString(36)
        .substr(2, 9)}`;
      sessionStorage.setItem(SESSION_ID_KEY, sessionId);
    }
    return sessionId;
  };

  // Helper function to create token for verified email
  const createTokenForVerifiedEmail = useCallback(
    async (emailAddress: string) => {
      const sessionId = getSessionId();

      // First try the refresh-token endpoint (same as API client uses)
      try {
        const refreshResponse = await fetch("/demo/refresh-token/", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            email: emailAddress,
            session_id: sessionId,
          }),
        });

        if (refreshResponse.ok) {
          const refreshData = await refreshResponse.json();
          if (refreshData.token && !refreshData.error) {
            storeTokenWithExpiry(
              refreshData.token,
              refreshData.expires_in,
              VERIFIED_TOKEN_EXPIRY_SECONDS,
              TOKEN_STORAGE_KEY,
              TOKEN_EXPIRY_KEY
            );
            sessionStorage.setItem(VERIFICATION_STATUS_KEY, "verified");
            setToken(refreshData.token);
            setIsEmailVerified(true);
            setVerificationState("verified");
            return; // Success, exit early
          }
        }
      } catch {
        // Continue to fallback
      }

      // Fallback to create-token endpoint if refresh fails
      try {
        const tokenResponse = await fetch("/guest/create-token/", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            email: emailAddress,
          }),
        });

        if (tokenResponse.ok) {
          const tokenData = await tokenResponse.json();
          if (tokenData.token && !tokenData.error) {
            storeTokenWithExpiry(
              tokenData.token,
              tokenData.expires_in,
              GUEST_TOKEN_EXPIRY_SECONDS,
              TOKEN_STORAGE_KEY,
              TOKEN_EXPIRY_KEY
            );
            sessionStorage.setItem(VERIFICATION_STATUS_KEY, "verified");
            setToken(tokenData.token);
            setIsEmailVerified(true);
            setVerificationState("verified");
          }
        }
      } catch (error) {
        console.warn("Failed to auto-generate token:", error);
      }
    },
    []
  );

  // Load saved stuff on mount and check URL parameters
  useEffect(() => {
    // Check URL parameters for verification redirect
    const urlParams = new URLSearchParams(window.location.search);
    const verified = urlParams.get("verified");
    const urlToken = urlParams.get("token");
    const urlEmail = urlParams.get("email");

    // If we have verification data in URL, use it immediately
    if (verified === "true" && urlToken && urlEmail) {
      // URL token comes from verify endpoint which uses 24 hour expiry
      const expiryTime = calculateTokenExpiry(
        undefined,
        VERIFIED_TOKEN_EXPIRY_SECONDS
      );
      sessionStorage.setItem(TOKEN_STORAGE_KEY, urlToken);
      sessionStorage.setItem(EMAIL_STORAGE_KEY, urlEmail);
      sessionStorage.setItem(TOKEN_EXPIRY_KEY, expiryTime.toString());
      sessionStorage.setItem(VERIFICATION_STATUS_KEY, "verified");

      // Also store in localStorage for cross-tab persistence
      localStorage.setItem(EMAIL_LOCAL_STORAGE_KEY, urlEmail);
      addVerifiedEmail(urlEmail);

      setToken(urlToken);
      setEmail(urlEmail);
      setIsEmailVerified(true);
      setVerificationState("verified");

      // Clean up URL parameters
      window.history.replaceState({}, document.title, window.location.pathname);
      return;
    }

    // Load from session storage first
    const storedToken = sessionStorage.getItem(TOKEN_STORAGE_KEY);
    const storedEmail = sessionStorage.getItem(EMAIL_STORAGE_KEY);
    const tokenExpiry = sessionStorage.getItem(TOKEN_EXPIRY_KEY);
    const verificationStatus = sessionStorage.getItem(VERIFICATION_STATUS_KEY);

    // Also check localStorage for persistent email
    const persistentEmail = localStorage.getItem(EMAIL_LOCAL_STORAGE_KEY);

    // Use session email first, fallback to persistent email
    const emailToUse = storedEmail || persistentEmail;

    if (emailToUse) {
      setEmail(emailToUse);

      // If no session email but we have persistent email, update session
      if (!storedEmail && persistentEmail) {
        sessionStorage.setItem(EMAIL_STORAGE_KEY, persistentEmail);
      }
    }

    // Check if verified in this session
    if (verificationStatus === "verified") {
      setIsEmailVerified(true);
      setVerificationState("verified");
    } else if (emailToUse && isEmailPreviouslyVerified(emailToUse)) {
      // More lenient: if email was previously verified, mark as verified
      setIsEmailVerified(true);
      setVerificationState("verified");
      sessionStorage.setItem(VERIFICATION_STATUS_KEY, "verified");
    }

    if (storedToken && tokenExpiry) {
      const expiryTime = parseInt(tokenExpiry, 10);
      if (
        Date.now() < expiryTime &&
        (verificationStatus === "verified" ||
          (emailToUse && isEmailPreviouslyVerified(emailToUse)))
      ) {
        setToken(storedToken);
      } else if (Date.now() >= expiryTime) {
        // Token expired - clean up old token
        sessionStorage.removeItem(TOKEN_STORAGE_KEY);
        sessionStorage.removeItem(TOKEN_EXPIRY_KEY);

        // But if email was previously verified, auto-generate new token
        if (emailToUse && isEmailPreviouslyVerified(emailToUse)) {
          createTokenForVerifiedEmail(emailToUse);
        }
      }
    } else if (emailToUse && isEmailPreviouslyVerified(emailToUse)) {
      // Auto-generate token for previously verified email if missing
      createTokenForVerifiedEmail(emailToUse);
    }

    // Mark initialization as complete
    setIsInitializing(false);
  }, [createTokenForVerifiedEmail]);

  // Listen for storage events (cross-tab communication)
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      // Listen for verification updates from other tabs
      if (e.key === VERIFICATION_LOCAL_STORAGE_KEY && e.newValue) {
        const verifiedEmails = JSON.parse(e.newValue);
        const currentEmail =
          email ||
          sessionStorage.getItem(EMAIL_STORAGE_KEY) ||
          localStorage.getItem(EMAIL_LOCAL_STORAGE_KEY);

        if (
          currentEmail &&
          verifiedEmails.includes(currentEmail) &&
          !isEmailVerified
        ) {
          // Email was just verified in another tab
          setIsEmailVerified(true);
          setVerificationState("verified");
          sessionStorage.setItem(VERIFICATION_STATUS_KEY, "verified");

          // Auto-generate token if we don't have one
          if (!token) {
            createTokenForVerifiedEmail(currentEmail);
          }
        }
      }

      // Listen for email updates
      if (e.key === EMAIL_LOCAL_STORAGE_KEY && e.newValue && !email) {
        setEmail(e.newValue);
        sessionStorage.setItem(EMAIL_STORAGE_KEY, e.newValue);
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, [email, token, isEmailVerified, createTokenForVerifiedEmail]);

  // Submit email for verification (creates demo user and sends verification email)
  const submitEmail = async (emailAddress: string) => {
    setIsLoading(true);
    setError(null);

    try {
      const sessionId = getSessionId();
      // Capture current URL to preserve user's state
      const currentUrl = window.location.href;

      const response = await fetch("/demo/request-verification/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: emailAddress,
          session_id: sessionId,
          return_url: currentUrl,
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(
          data.error || `Failed to submit email: ${response.statusText}`
        );
      }

      const data = await response.json();
      if (data.error) {
        throw new Error(data.error);
      }

      // Store email in both session and local storage
      sessionStorage.setItem(EMAIL_STORAGE_KEY, emailAddress);
      localStorage.setItem(EMAIL_LOCAL_STORAGE_KEY, emailAddress);
      setEmail(emailAddress);
      setVerificationState("pending");
      setIsLoading(false);
    } catch (err) {
      const errorMsg =
        err instanceof Error ? err.message : "Failed to submit email";
      setError(errorMsg);
      setVerificationState("error");
      setIsLoading(false);
      throw err;
    }
  };

  // Verify email with token from magic link
  const verifyEmail = async (verificationToken: string) => {
    setIsLoading(true);
    setError(null);

    try {
      const sessionId = getSessionId();
      // Get email from session storage (might have been set by the verification route)
      const storedEmail = sessionStorage.getItem(EMAIL_STORAGE_KEY) || email;

      if (!storedEmail) {
        throw new Error("Email address is required for verification");
      }

      const response = await fetch("/demo/verify-email/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          token: verificationToken, // Changed from verification_token to token
          email: storedEmail,
          session_id: sessionId,
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(
          data.error || `Verification failed: ${response.statusText}`
        );
      }

      const data = await response.json();
      if (data.error) {
        throw new Error(data.error);
      }

      // Use the email that was already stored (from URL or previous submission)
      const verifiedEmail = data.email || storedEmail;
      if (!verifiedEmail) {
        throw new Error("Email not found after verification");
      }

      // Store email in session, local storage, and state
      sessionStorage.setItem(EMAIL_STORAGE_KEY, verifiedEmail);
      localStorage.setItem(EMAIL_LOCAL_STORAGE_KEY, verifiedEmail);
      addVerifiedEmail(verifiedEmail); // This will trigger storage event for other tabs
      setEmail(verifiedEmail);

      // Now get a token for the verified user
      const tokenResponse = await fetch("/guest/create-token/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: verifiedEmail,
        }),
      });

      if (!tokenResponse.ok) {
        throw new Error(
          `Failed to get token after verification: ${tokenResponse.statusText}`
        );
      }

      const tokenData = await tokenResponse.json();
      if (tokenData.error) {
        throw new Error(tokenData.error);
      }

      // Store token and mark as verified
      storeTokenWithExpiry(
        tokenData.token,
        tokenData.expires_in,
        GUEST_TOKEN_EXPIRY_SECONDS,
        TOKEN_STORAGE_KEY,
        TOKEN_EXPIRY_KEY
      );
      sessionStorage.setItem(VERIFICATION_STATUS_KEY, "verified");

      setToken(tokenData.token);
      setIsEmailVerified(true);
      setVerificationState("verified");
      setIsLoading(false);
    } catch (err) {
      const errorMsg =
        err instanceof Error ? err.message : "Verification failed";
      setError(errorMsg);
      setVerificationState("error");
      setIsLoading(false);
      throw err;
    }
  };

  // Resend verification email
  const resendVerificationEmail = async () => {
    if (!email) {
      throw new Error("No email address available");
    }

    setIsLoading(true);
    setError(null);

    try {
      const sessionId = getSessionId();
      // Capture current URL to preserve user's state
      const currentUrl = window.location.href;

      const response = await fetch("/demo/resend-verification/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email,
          session_id: sessionId,
          return_url: currentUrl,
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(
          data.error || `Failed to resend: ${response.statusText}`
        );
      }

      const data = await response.json();
      if (data.error) {
        throw new Error(data.error);
      }

      setIsLoading(false);
    } catch (err) {
      const errorMsg =
        err instanceof Error ? err.message : "Failed to resend email";
      setError(errorMsg);
      setIsLoading(false);
      throw err;
    }
  };

  // Refresh token (only works if verified)
  const refreshToken = async () => {
    if (!email || !isEmailVerified) {
      throw new Error("Cannot refresh token: email not verified");
    }

    setIsLoading(true);
    setError(null);

    try {
      const sessionId = getSessionId();
      const response = await fetch("/demo/refresh-token/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email,
          session_id: sessionId,
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to refresh token: ${response.statusText}`);
      }

      const data = await response.json();
      if (data.error) {
        throw new Error(data.error);
      }

      // Update token
      storeTokenWithExpiry(
        data.token,
        data.expires_in,
        VERIFIED_TOKEN_EXPIRY_SECONDS,
        TOKEN_STORAGE_KEY,
        TOKEN_EXPIRY_KEY
      );
      setToken(data.token);
      setIsLoading(false);
    } catch (err) {
      const errorMsg =
        err instanceof Error ? err.message : "Token refresh failed";
      setError(errorMsg);
      setIsLoading(false);
      throw err;
    }
  };

  const logout = () => {
    sessionStorage.removeItem(TOKEN_STORAGE_KEY);
    sessionStorage.removeItem(TOKEN_EXPIRY_KEY);
    sessionStorage.removeItem(EMAIL_STORAGE_KEY);
    sessionStorage.removeItem(VERIFICATION_STATUS_KEY);
    sessionStorage.removeItem(SESSION_ID_KEY);
    // Also clear localStorage data
    localStorage.removeItem(EMAIL_LOCAL_STORAGE_KEY);
    localStorage.removeItem(VERIFICATION_LOCAL_STORAGE_KEY);
    setToken(null);
    setEmail(null);
    setIsEmailVerified(false);
    setVerificationState("initial");
    setError(null);
  };

  const isAuthenticated = !!token && !!email && isEmailVerified;

  const value: AuthContextType = {
    token,
    email,
    isAuthenticated,
    isEmailVerified,
    verificationState,
    isLoading,
    isInitializing,
    error,
    submitEmail,
    verifyEmail,
    resendVerificationEmail,
    refreshToken,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
